/**
 * MathCoachGuidanceScreen
 * Shown when a premium user navigates to /math-coach but no active math coach session exists.
 *
 * Behaviour:
 *   - On mount: starts polling GET /sessions/math-coach/active every 5 seconds (AC-002.6)
 *   - If active session found: auto-navigates to /session/{sessionId} (AC-002.4)
 *   - If no session after 2 minutes: shows "Still waiting..." with a "Check Again" button (AC-002.7)
 *   - On error: shows generic error message with retry (edge case 4)
 */

import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Calculator, RefreshCw } from 'lucide-react';
import { useMathCoachSession } from '@/hooks/useMathCoachSession';
import { AppLogo } from '@/components/AppLogo';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { Button } from '@/components/ui/button';

export function MathCoachGuidanceScreen() {
  const navigate = useNavigate();
  const {
    session,
    isLoading,
    error,
    isPolling,
    isTimedOut,
    startPolling,
    stopPolling,
  } = useMathCoachSession();

  // Start polling on mount; stop on unmount (AC-002.6)
  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  // Auto-navigate when an active session is detected (AC-002.4)
  useEffect(() => {
    if (session && session.sessionId) {
      navigate(`/session/${session.sessionId}`, { replace: true });
    }
  }, [session, navigate]);

  // Initial loading state (first fetch in progress)
  if (isLoading) {
    return <LoadingState message="Checking for active session..." />;
  }

  // Non-404 error state (500, network, etc.) — edge case 4
  if (error) {
    return (
      <ErrorState
        message="Something went wrong. Please try again."
        onRetry={() => startPolling()}
      />
    );
  }

  // Guidance screen: shown when no active session exists (session is null)
  return (
    <div className="min-h-screen bg-gradient-to-b from-otter-paper to-otter-sand">
      {/* Header — matches dashboard header pattern */}
      <header className="border-b border-gray-200/50 bg-otter-paper/80 backdrop-blur-sm px-3 md:px-4 py-2 md:py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="text-sm text-otter-earth-500 hover:text-otter-earth-700 flex items-center gap-0.5"
            aria-label="Back to dashboard"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <AppLogo />
        </div>
      </header>

      {/* Main guidance content */}
      <main className="max-w-lg mx-auto px-4 py-12 md:py-20 text-center">
        {/* Math icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-6">
          <Calculator className="h-8 w-8 text-violet-600" aria-hidden="true" />
        </div>

        {/* Heading — exact text from AC-002.5 */}
        <h1 className="text-2xl md:text-3xl font-bold text-otter-earth-800 mb-4">
          Start Your Math Coach
        </h1>

        {/* Body text — exact instruction from AC-002.5 */}
        <p className="text-base md:text-lg text-otter-earth-700 mb-3">
          Open Claude and say{' '}
          <span className="font-semibold text-violet-700">
            &ldquo;Start a math coaching session&rdquo;
          </span>
        </p>

        {/* Subtext — confirms auto-detection from AC-002.5 */}
        <p className="text-sm text-gray-500 mb-8">
          Your session will appear here automatically once it starts.
        </p>

        {/* Polling status indicator: visible while actively polling */}
        {isPolling && !isTimedOut && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-4">
            <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>Checking for session...</span>
          </div>
        )}

        {/* Timeout state — AC-002.7: shown after 2-minute timeout */}
        {isTimedOut && (
          <div className="space-y-3 mb-4">
            <p className="text-sm text-gray-500">Still waiting...</p>
            <Button
              variant="outline"
              size="default"
              onClick={() => startPolling()}
              className="h-11"
              aria-label="Check again for active session"
            >
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              Check Again
            </Button>
          </div>
        )}

        {/* Back to dashboard button */}
        <div className="mt-8">
          <Button
            variant="ghost"
            size="default"
            onClick={() => navigate('/dashboard')}
            className="h-11 text-gray-500"
            aria-label="Back to dashboard"
          >
            Back to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
}
